/**
 * PropertyFilters – slide-out filter drawer for property list
 * Mirrors Angular property-list filter form
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated } from '@/features/auth/authSlice'
import { useUserRole } from '@/hooks/useUserRole'
import {
  getCompaniesForProperty,
  getOrganizationsForProperty,
  getPmsPropertyCodes,
} from '../services/propertyService'
import type { PropertyFiltersProps } from '../types'
import { cityFilterOptionLabel, dropJunkCityNames } from '@/lib/utils/cityFilterDisplay'
import { getStates } from '@/lib/api/statesService'
import { shouldRunOrganizationTypeaheadSearch } from '@/features/company/utils/companyListSearchLogic'
import { Filter, X } from 'lucide-react'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import styles from '../styles/propertyFilters.module.css'

/** Same as company-list filters: parse `/city` body whether direct or wrapped (matches Angular). */
function extractCityDataFromResponse(response: unknown): Record<string, string[]> {
  const raw =
    response && typeof response === 'object' && 'data' in response
      ? (response as { data?: unknown }).data
      : response
  if (!raw || typeof raw !== 'object') return {}
  const obj = raw as Record<string, unknown>
  if (obj.cities && typeof obj.cities === 'object' && !Array.isArray(obj.cities))
    return obj.cities as Record<string, string[]>
  if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data))
    return obj.data as Record<string, string[]>
  const entries = Object.entries(obj).filter(
    ([_, v]) => Array.isArray(v) && v.every((x: unknown) => typeof x === 'string'),
  )
  return entries.length ? (Object.fromEntries(entries) as Record<string, string[]>) : {}
}

function isFilterValueSelected(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  return true
}

function filterSelectWrapClass(value: unknown): string {
  const filled = isFilterValueSelected(value)
  return [
    'relative',
    styles.pillFilterWrapperVertical,
    filled ? styles.pillFilterFilled : styles.pillFilterEmpty,
  ].join(' ')
}

const FILTER_TEXT_INPUT_CLASS =
  'w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] placeholder:text-slate-400'

function filterTextInputClass(value: unknown): string {
  return [
    FILTER_TEXT_INPUT_CLASS,
    isFilterValueSelected(value) ? 'bg-slate-50' : 'bg-white',
  ].join(' ')
}


export function PropertyFilters({
  filters,
  onFiltersChange,
  onReset,
  isOpen,
  onClose,
}: PropertyFiltersProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const { isLibertyRole, isCharityRole, orgId, orgName, companyId, companyName, hasPolicy } = useUserRole()
  const showAdvanceSearch = hasPolicy(['property_config_view'])

  const [organizations, setOrganizations] = useState<SearchableSelectOption[]>([])
  const [loadingOrgs, setLoadingOrgs] = useState(false)
  const orgRequestIdRef = useRef(0)
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [states, setStates] = useState<SearchableSelectOption[]>([])
  const [cities, setCities] = useState<SearchableSelectOption[]>([])
  const [pmsCodes, setPmsCodes] = useState<SearchableSelectOption[]>([])
  const [loadingPmsCodes, setLoadingPmsCodes] = useState(false)

  /** Angular: org select always visible; editable for Liberty, read-only when role has a fixed org. */
  const orgSelectLocked = !isLibertyRole && !!orgId
  const showOrganizationFilter = isLibertyRole || !!orgId
  /** Angular getAccessiblePermission: role with company_id → readOnlyControl includes company. */
  const companySelectLocked = !!companyId

  const loadOrganizations = useCallback(
    async (search: string) => {
      if (!isAuthenticated || !isLibertyRole) return
      const requestId = ++orgRequestIdRef.current
      setLoadingOrgs(true)
      try {
        const data = await getOrganizationsForProperty(search)
        if (requestId !== orgRequestIdRef.current) return
        const mapped = (data as SearchableSelectOption[]).map((o) => ({
          value: String(o.value),
          label: o.label,
        }))
        setOrganizations((prev) => {
          const selectedId =
            filters.org_id != null && filters.org_id !== '' ? String(filters.org_id) : ''
          if (!selectedId || mapped.some((o) => String(o.value) === selectedId)) return mapped
          const label =
            (filters.org_name && String(filters.org_name)) ||
            prev.find((o) => String(o.value) === selectedId)?.label
          if (!label) return mapped
          return [{ value: selectedId, label }, ...mapped]
        })
      } catch (e) {
        if (requestId !== orgRequestIdRef.current) return
        console.error(e)
      } finally {
        if (requestId === orgRequestIdRef.current) {
          setLoadingOrgs(false)
        }
      }
    },
    [isAuthenticated, isLibertyRole, filters.org_id, filters.org_name],
  )

  /** Angular `searchOrganization`: even-length only (empty allowed). */
  const searchOrganizations = useCallback(
    (search: string) => {
      if (!shouldRunOrganizationTypeaheadSearch(search)) return
      void loadOrganizations(search)
    },
    [loadOrganizations],
  )

  const loadCompanies = useCallback(
    async (search: string) => {
      if (!isAuthenticated || companySelectLocked) return
      setLoadingCompanies(true)
      try {
        const data = await getCompaniesForProperty(
          search,
          filters.org_id,
          filters.listed || 'all'
        )
        setCompanies(data as SearchableSelectOption[])
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingCompanies(false)
      }
    },
    [isAuthenticated, companySelectLocked, filters.org_id, filters.listed]
  )

  useEffect(() => {
    if (!isAuthenticated) return
    const fetchStates = async () => {
      try {
        const stateList = await getStates({ zip_flag: '1' })
        const stateOptions: SearchableSelectOption[] = stateList
          .map((s) => ({
            value: s.state_code || s.abbreviation || '',
            label: s.name || s.state_name || s.state_code || s.abbreviation || '',
          }))
          .filter((o) => o.value && o.label)
        setStates(stateOptions)
      } catch (e) {
        console.error('Failed to load states:', e)
      }
    }
    fetchStates()
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return
    const fetchCities = async () => {
      try {
        const { default: apiClient } = await import('@/lib/api/apiClient')
        const response = await apiClient.get('/city')
        const cityData = extractCityDataFromResponse(response)
        if (filters.state) {
          const stateCities = dropJunkCityNames(cityData[filters.state] || [])
          setCities(
            stateCities.map((city) => ({
              value: city,
              label: cityFilterOptionLabel(city),
            })) as SearchableSelectOption[],
          )
        } else {
          const merged: string[] = []
          Object.entries(cityData).forEach(([, list]: [string, unknown]) => {
            (Array.isArray(list) ? list : []).forEach((city: string) => merged.push(city))
          })
          const uniqueSorted = Array.from(new Set(dropJunkCityNames(merged))).sort((a, b) =>
            a.localeCompare(b),
          )
          setCities(
            uniqueSorted.map((city) => ({
              value: city,
              label: cityFilterOptionLabel(city),
            })) as SearchableSelectOption[],
          )
        }
      } catch (e) {
        console.error('Failed to load cities:', e)
      }
    }
    fetchCities()
  }, [isAuthenticated, filters.state, isOpen])

  useEffect(() => {
    if (isAuthenticated && isLibertyRole && organizations.length === 0) {
      loadOrganizations('')
    }
  }, [isAuthenticated, isLibertyRole])

  /** Angular getAccessiblePermission: non-Liberty roles with org_id get a fixed org option. */
  useEffect(() => {
    if (isLibertyRole || !orgId) return
    const id = String(orgId)
    const name = (orgName && String(orgName).trim()) || (filters.org_name && String(filters.org_name).trim()) || id
    setOrganizations((prev) => {
      if (prev.some((o) => String(o.value) === id)) return prev
      return [{ value: id, label: name }]
    })
  }, [isLibertyRole, orgId, orgName, filters.org_name])

  /** Angular getAccessiblePermission: role with company_id gets a fixed company option. */
  useEffect(() => {
    if (!companyId) return
    const id = String(companyId)
    const name =
      (companyName && String(companyName).trim()) ||
      (filters.company_name && String(filters.company_name).trim()) ||
      id
    setCompanies((prev) => {
      if (prev.some((o) => String(o.value) === id)) return prev
      return [{ value: id, label: name }]
    })
  }, [companyId, companyName, filters.company_name])

  // Angular setDefaultRequestParam: seed org dropdown from session org_id + org_name
  useEffect(() => {
    if (!filters.org_id || !filters.org_name) return
    const id = String(filters.org_id)
    const name = String(filters.org_name)
    setOrganizations((prev) => {
      if (prev.some((o) => String(o.value) === id)) return prev
      return [{ value: id, label: name }, ...prev]
    })
  }, [filters.org_id, filters.org_name])

  useEffect(() => {
    if (isAuthenticated && !companySelectLocked) {
      loadCompanies('')
    }
  }, [isAuthenticated, companySelectLocked, loadCompanies])

  const loadPmsCodes = useCallback(async () => {
    if (!isAuthenticated || !showAdvanceSearch) return
    setLoadingPmsCodes(true)
    try {
      const data = await getPmsPropertyCodes(undefined, filters.company_id)
      setPmsCodes(data.map(({ value, label }) => ({ value, label })))
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingPmsCodes(false)
    }
  }, [isAuthenticated, showAdvanceSearch, filters.company_id])

  useEffect(() => {
    if (isAuthenticated && showAdvanceSearch) {
      void loadPmsCodes()
    }
  }, [isAuthenticated, showAdvanceSearch, loadPmsCodes])

  /** Include session-stored label so the trigger shows the name before/without a matching API row. */
  const organizationSelectOptions = useMemo((): SearchableSelectOption[] => {
    const list = [...organizations]
    const oid =
      filters.org_id !== undefined && filters.org_id !== null && filters.org_id !== ''
        ? String(filters.org_id)
        : ''
    const label = (filters.org_name && String(filters.org_name).trim()) || ''
    if (oid && label && !list.some((o) => String(o.value) === oid)) {
      list.unshift({ value: oid, label })
    }
    return list
  }, [organizations, filters.org_id, filters.org_name])

  const companySelectOptions = useMemo((): SearchableSelectOption[] => {
    const list = [...companies]
    const cid =
      filters.company_id !== undefined && filters.company_id !== null && filters.company_id !== ''
        ? String(filters.company_id)
        : companyId
          ? String(companyId)
          : ''
    const label =
      (filters.company_name && String(filters.company_name).trim()) ||
      (companyName && String(companyName).trim()) ||
      ''
    if (cid && label && !list.some((o) => String(o.value) === cid)) {
      list.unshift({ value: cid, label })
    }
    return list
  }, [companies, filters.company_id, filters.company_name, companyId, companyName])

  const prevDrawerOpenRef = useRef(isOpen)
  useEffect(() => {
    if (prevDrawerOpenRef.current && !isOpen && isAuthenticated) {
      if (!companySelectLocked) loadCompanies('')
      if (isLibertyRole) loadOrganizations('')
    }
    prevDrawerOpenRef.current = isOpen
  }, [isOpen, isAuthenticated, isLibertyRole, companySelectLocked, loadCompanies, loadOrganizations])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  useBodyScrollLock(isOpen)

  return (
    <>
      <div
        data-testid="property-filters-backdrop"
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[2147483646] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        data-testid="property-filters-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:max-w-sm bg-white shadow-2xl z-[2147483647] transform transition-transform duration-300 ease-in-out flex flex-col overflow-y-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-slate-800">
            <Filter className="h-5 w-5 text-[rgb(21_151_255)]" />
            <h2 className="text-lg font-bold">Filters</h2>
          </div>
          <button
            data-testid="property-filters-close-button"
            onClick={onClose}
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {showOrganizationFilter && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Organization
              </label>
              <div className={filterSelectWrapClass(filters.org_id)}>
                <SearchableSelect
                  placeholder="All Organizations"
                  options={organizationSelectOptions}
                  value={filters.org_id ? String(filters.org_id) : ''}
                  onChange={(val) => {
                    const orgIdVal = Array.isArray(val) ? val[0] : val
                    if (!orgIdVal) {
                      onFiltersChange({
                        org_id: undefined,
                        org_name: undefined,
                        ...(companySelectLocked
                          ? {}
                          : { company_id: undefined, company_name: undefined }),
                      })
                      return
                    }
                    const label = organizationSelectOptions.find(
                      (o) => String(o.value) === String(orgIdVal),
                    )?.label
                    // Angular organizationSelected: clear company when Liberty/Charity changes org
                    const clearCompany =
                      !companySelectLocked &&
                      (isLibertyRole || isCharityRole) &&
                      String(orgIdVal) !== String(filters.org_id ?? '')
                    onFiltersChange({
                      org_id: orgIdVal,
                      org_name: label || undefined,
                      ...(clearCompany
                        ? { company_id: undefined, company_name: undefined }
                        : {}),
                    })
                  }}
                  onSearch={orgSelectLocked ? undefined : searchOrganizations}
                  onPasteSearch={orgSelectLocked ? undefined : loadOrganizations}
                  immediateSearchOnPaste={!orgSelectLocked}
                  onOpen={orgSelectLocked ? undefined : () => loadOrganizations('')}
                  searchPlaceholder="Search By Organizations Name"
                  loading={loadingOrgs}
                  disabled={orgSelectLocked}
                  triggerDataTestId="property-filters-org-select"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Company
            </label>
            <div className={filterSelectWrapClass(filters.company_id)}>
              <SearchableSelect
                placeholder="All Companies"
                options={companySelectOptions}
                value={filters.company_id ? String(filters.company_id) : ''}
                onChange={(val) => {
                  if (companySelectLocked) return
                  const v = Array.isArray(val) ? val[0] : val
                  if (!v) {
                    onFiltersChange({ company_id: undefined, company_name: undefined })
                    return
                  }
                  const opt = companySelectOptions.find((o) => o.value === v)
                  onFiltersChange({
                    company_id: v,
                    company_name: opt?.label,
                  })
                }}
                onSearch={companySelectLocked ? undefined : loadCompanies}
                onOpen={companySelectLocked ? undefined : () => loadCompanies('')}
                loading={loadingCompanies}
                disabled={companySelectLocked}
                triggerDataTestId="property-filters-company-select"
              />
            </div>
          </div>

          {!!filters.org_id && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Listed Status
              </label>
              <div className={filterSelectWrapClass(filters.listed)}>
                <SearchableSelect
                  triggerDataTestId="property-filters-listed-select"
                  placeholder="All"
                  searchable={false}
                  options={[
                    { value: '', label: 'All' },
                    { value: '1', label: 'Active' },
                    { value: '0', label: 'Not Active' },
                  ]}
                  value={filters.listed || ''}
                  onChange={(val) => {
                    const v = Array.isArray(val) ? val[0] : val
                    onFiltersChange({ listed: v || undefined })
                  }}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              State
            </label>
            <div className={filterSelectWrapClass(filters.state)}>
              <SearchableSelect
                placeholder="Select State"
                options={states}
                value={filters.state || ''}
                onChange={(val) => {
                  const v = Array.isArray(val) ? val[0] : val
                  onFiltersChange({ state: v || undefined, city: undefined })
                }}
                triggerDataTestId="property-filters-state-select"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              City
            </label>
            <div className={filterSelectWrapClass(filters.city)}>
              <SearchableSelect
                placeholder="Select City"
                options={cities}
                value={filters.city || ''}
                onChange={(val) => {
                  const v = Array.isArray(val) ? val[0] : val
                  onFiltersChange({ city: v || undefined })
                }}
                triggerDataTestId="property-filters-city-select"
              />
            </div>
          </div>

          {isLibertyRole && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Liberty ID
              </label>
              <input
                data-testid="property-filters-liberty-id-input"
                type="text"
                className={filterTextInputClass(filters.liberty_id)}
                placeholder="Liberty ID"
                value={filters.liberty_id || ''}
                onChange={(e) =>
                  onFiltersChange({ liberty_id: e.target.value || undefined })
                }
              />
            </div>
          )}

          {showAdvanceSearch && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                PMS Property Code
              </label>
              <div className={filterSelectWrapClass(filters.pms_property_code)}>
                <SearchableSelect
                  triggerDataTestId="property-filters-pms-property-code-select"
                  placeholder="Select PMS property code"
                  searchPlaceholder="Search PMS property code"
                  options={pmsCodes}
                  value={filters.pms_property_code || []}
                  onChange={(val) => {
                    const codes = Array.isArray(val) ? val : val ? [val] : []
                    onFiltersChange({
                      pms_property_code: codes.length > 0 ? codes : undefined,
                    })
                  }}
                  onOpen={() => {
                    void loadPmsCodes()
                  }}
                  multiple
                  selectedChipsLayout="flex-wrap"
                  loading={loadingPmsCodes}
                />
              </div>
            </div>
          )}

          {showAdvanceSearch && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Advance Option
              </label>
              <div className={filterSelectWrapClass(filters.advance_search)}>
                <SearchableSelect
                  triggerDataTestId="property-filters-advance-search-select"
                  placeholder="Select Advance Option"
                  searchable={false}
                  menuPlacement="bottom"
                  options={[
                    { value: '', label: 'Select Advance Option' },
                    { value: 'yardi_properties', label: 'Yardi Properties' },
                    { value: 'unmapped_pms_properties', label: 'Unmapped PMS Properties' },
                  ]}
                  value={filters.advance_search || ''}
                  onChange={(val) => {
                    const v = Array.isArray(val) ? val[0] : val
                    onFiltersChange({
                      advance_search: v || undefined,
                    })
                  }}
                />
              </div>
              {filters.advance_search === 'unmapped_pms_properties' && (
                <p
                  className="text-xs text-slate-500 leading-relaxed"
                  data-testid="property-filters-unmapped-hint"
                >
                  Select a company to find Liberty properties with no PMS property code for that PMC.
                </p>
              )}
            </div>
          )}

          {isLibertyRole && (
            <div className="flex items-center gap-3">
              <input
                data-testid="property-filters-deleted-checkbox"
                type="checkbox"
                id="check_for_delete"
                className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                checked={!!filters.check_for_delete}
                onChange={(e) =>
                  onFiltersChange({
                    check_for_delete: e.target.checked || undefined,
                  })
                }
              />
              <label
                htmlFor="check_for_delete"
                className="text-sm text-slate-700 font-medium"
                data-testid="property-filters-deleted-label"
              >
                Check For Deleted
              </label>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-4">
          <button
            data-testid="property-filters-reset-button"
            onClick={onReset}
            className="filter-drawer-reset-all"
            type="button"
          >
            Reset All
          </button>
          <button
            data-testid="property-filters-done-button"
            onClick={onClose}
            className={cn(primaryEntityActionButtonClass, 'flex-1')}
            type="button"
          >
            Done
          </button>
        </div>
      </div>
    </>
  )
}
